/**
 * @file device.ts
 * @description GCOME_AP 磁轴键盘设备容器，负责生命周期、Raw AP 命令和安全只读探测。
 */

import { BaseDevice } from '../../../runtime/BaseDevice'
import type { HidChannel } from '../../../channels/hid/HidChannel'
import type { DeviceManifest } from '../../../types/manifest'
import { ReportEvents } from '../../../events/ReportEvents'
import { GcomeApCommandClient } from '../../../chips/gcome-ap/protocols/client'
import { GCOME_AP_CMD } from '../../../chips/gcome-ap/protocols/cmd'
import { decodeGcomeApFrame } from '../../../chips/gcome-ap/protocols/frame'
import { parseGcomeApInfo } from '../../../chips/gcome-ap/protocols/parser'
import { parseGcomeApActiveReport } from '../../../chips/gcome-ap/utils/active-report'
import type { GcomeApActiveReport } from '../../../chips/gcome-ap/utils/active-report'
import type {
  GcomeApFrame,
  GcomeApRawCommandRequest,
  GcomeApRawCommandResult,
} from '../../../chips/gcome-ap/protocols/types'
import { GcomeApKeyboardFirmware } from '../../../chips/gcome-ap/feature-impls/keyboard/firmware'
import { GcomeApKeyboardConfig } from '../../../chips/gcome-ap/feature-impls/keyboard/config'
import { GcomeApKeyboardLighting } from '../../../chips/gcome-ap/feature-impls/keyboard/keyboard-lighting'
import { GcomeApKeyboardKeys } from '../../../chips/gcome-ap/feature-impls/keyboard/keyboard-keys'
import { GcomeApKeyTravel } from '../../../chips/gcome-ap/feature-impls/keyboard/key-travel'
import { GcomeApKeyboardPerfCfg } from '../../../chips/gcome-ap/feature-impls/keyboard/keyboard-performance'
import { getKeyboardPerfHostHooks } from '../../../features/keyboard-performance/hostHooks'

const CONN_WIRED = 0xff as const
const CONN_WIRELESS = 0xa5 as const

/**
 * 驱动探测回复（协议文档「5. 键盘打开网页驱动或客户端驱动」，识图复核 2026-08-15）：
 * 键盘按键触发上报 0xC1 0xFF 0xFF 0xFF 0xFF 请求打开驱动；
 * 已安装桌面驱动的后台进程回复 0xC2 0xFF 0xFF 0xFF 0xFF，
 * 固件 1S 内收到回复则不启用 CMD 打开网驱（改为打开客户端驱动）。
 * 注：早期整理将上报/回复误作 0xCF，PDF 原文为 0xC1/0xC2（已确认）。
 */
const DRIVER_PROBE_REPLY: number[] = [0xc2, 0xff, 0xff, 0xff, 0xff] as const

export class GcomeApKeyboardDevice extends BaseDevice {
  public readonly sdkVersion = '0.0.1'
  private readonly apClient: GcomeApCommandClient
  /** 上次上报的配置分组（0xA1，用于板载切换同步）。 */
  private lastProfile: number | undefined
  /** 上次上报的灯光信息（0xA3，用于灯效/亮度/速度变化同步）。 */
  private lastLighting: (GcomeApActiveReport & { type: 'lighting' }) | undefined
  /** 上次上报的电池状态（0xFA 0xFB 0x07）。 */
  private lastBattery: { percent: number; chargingCode: number } | undefined

  constructor(channel: HidChannel, manifest: DeviceManifest) {
    super(channel, manifest)
    this.apClient = new GcomeApCommandClient(channel, (task) => this.runAtomic(() => task()))
    this.detectConnectionType()

    this.features.firmware = new GcomeApKeyboardFirmware(this)
    this.features.config = new GcomeApKeyboardConfig(this)
    this.features.keyboardLighting = new GcomeApKeyboardLighting(this)
    this.features.keyboardKeys = new GcomeApKeyboardKeys(this)
    this.features.keyTravel = new GcomeApKeyTravel(this)
    this.features.keyboardPerfCfg = new GcomeApKeyboardPerfCfg(this)
  }

  public async sendRawApCommand(
    request: GcomeApRawCommandRequest,
  ): Promise<GcomeApRawCommandResult> {
    return this.apClient.request(request)
  }

  /**
   * 回复驱动探测（模拟「已安装桌面驱动」应答）：
   * 键盘按键触发上报 0xC1 0xFF 0xFF 0xFF 0xFF 请求打开驱动，调用本方法写回驱动应答帧
   * 0xC2 0xFF 0xFF 0xFF 0xFF；固件在 1S 超时窗口内收到回复后不会启用 CMD 打开网驱，
   * 而由回复方打开客户端驱动。回复走原始 output report（非 AP 帧，不经命令队列）。
   */
  public async replyDriverProbe(payload: number[] = DRIVER_PROBE_REPLY): Promise<void> {
    if (!this.channel.isConnected) return
    const data = new Uint8Array(payload)
    await this.channel.sendOutputReport(0x00, data)
    this.emit('gcome-ap:driver-reply', Array.from(data))
  }

  /**
   * 进入设备前连通探测：一次 GetInfo 同时确认在线，并在 SDK 内判定是否需提示 USB2+16K。
   * 返回值只表示能否进入；宿主弹窗由 KeyboardPerfHostHooks 承接，应用层不解析协议字段。
   */
  async checkConnect(): Promise<boolean> {
    if (!this.channel.isConnected) return false
    try {
      const res = await this.sendRawApCommand({ command: GCOME_AP_CMD.getInfo })
      const info = parseGcomeApInfo(res.response.data)
      if (!info.isUsb3) {
        void this.notifyUsb2With16kDriver()
      }
      return true
    } catch (_error) {
      return false
    }
  }

  /** 当前非 USB3：通知宿主弹窗（未注册 hooks 时静默）。 */
  private async notifyUsb2With16kDriver(): Promise<void> {
    const hooks = getKeyboardPerfHostHooks()
    if (!hooks?.notifyUsb2With16kDriver) return
    try {
      await hooks.notifyUsb2With16kDriver()
    } catch (error) {
      console.warn('[GCOME_AP] notifyUsb2With16kDriver host hook failed', error)
    }
  }

  protected onConnected(): void {
    console.log(`[GcomeApKeyboardDevice] Connected: ${this.manifest.model}`)
    // 协议层连接模式（字节36）是权威状态：0x0f=有线，其余=无线/蓝牙；
    // 异步探测刷新 connectionType，失败静默回退构造时的 HID 启发式结果。
    // void this.refreshConnectionType()
  }

  /**
   * @deprecated getConnectionMode此方法gcome说无法靠这个区分有线无线，故先弃用refreshConnectionType，后续如果修复再启用
   * 用 AP 协议连接模式刷新连接类型（0x0f→有线；2.4G/蓝牙→无线）。
   */
  private async refreshConnectionType(): Promise<void> {
    try {
      const mode = await this.features.keyboardPerfCfg?.getConnectionMode()
      if (mode !== undefined) {
        this.connectionType = mode === 0x0f ? CONN_WIRED : CONN_WIRELESS
      }
    } catch (_error) {
      // 保持 detectConnectionType 的启发式结果
    }
  }

  protected handleReport(reportId: number, data: DataView): void {
    const buffer = new Uint8Array(data.buffer, data.byteOffset, data.byteLength)
    this.emit('report', reportId, buffer)

    const frame = this.tryDecodeReport(data)
    if (frame) {
      // 关键：AP 协议主动上报也走 64 字节帧，Web 测试页可直接监听该事件查看解析结果。
      this.emit('gcome-ap:frame', frame)
    }

    // 主动上报统一解析（按协议文档：0xA0 按键调试 / 0xA1 状态 / 0xFA 0xFB 电池与断连回连 /
    // 0xA3 灯光信息 / 0xA4 摇杆校准 / 0xC1 驱动探测），载体为 AP 帧 payload 或原始 input report。
    const active = parseGcomeApActiveReport(frame ? frame.data : buffer)
    if (active) {
      this.emit('gcome-ap:active', { ...active, raw: Array.from(frame ? frame.data : buffer) })
      this.syncActiveReport(active)
    }
  }

  /**
   * 主动上报 → 数据同步事件（参考 telink / wch-ch585m 键盘的上报同步模式）：
   * - 0xA1 状态 → KeyUserData + 分组变化时 CfgChanged
   * - 0xA3 灯光 → 变化时 EffectChanged / BrightnessChanged / SpeedChanged
   * - 0xFA 电池 → 变化时 Battery
   * - 0xA0 按键调试 → KeyCalibUpload
   */
  private syncActiveReport(active: GcomeApActiveReport): void {
    switch (active.type) {
      case 'key-debug':
        this.emit(ReportEvents.KeyCalibUpload, active.data)
        break
      case 'update': {
        this.emit(ReportEvents.KeyUserData, { fnLayer: active.fnLayer, profile: active.profile })
        if (this.lastProfile !== active.profile) {
          this.lastProfile = active.profile
          this.emit(ReportEvents.CfgChanged, { fnLayer: active.fnLayer, profile: active.profile })
        }
        break
      }
      case 'lighting': {
        const prev = this.lastLighting?.backlight
        const cur = active.backlight
        if (prev?.mode !== cur.mode) this.emit(ReportEvents.EffectChanged, cur.mode)
        if (prev?.level !== cur.level) this.emit(ReportEvents.BrightnessChanged, cur.level)
        if (prev?.speed !== cur.speed) this.emit(ReportEvents.SpeedChanged, cur.speed)
        this.lastLighting = active
        break
      }
      case 'battery': {
        const changed =
          this.lastBattery === undefined ||
          this.lastBattery.percent !== active.percent ||
          this.lastBattery.chargingCode !== active.chargingCode
        if (changed) {
          this.lastBattery = { percent: active.percent, chargingCode: active.chargingCode }
          this.emit(ReportEvents.Battery, {
            percent: active.percent,
            charging: active.chargingCode > 0,
          })
        }
        break
      }
      default:
        break
    }
  }

  private detectConnectionType(): void {
    const info = this.channel.deviceInfo
    if (!info) return

    const pids = Array.isArray(this.manifest.hardware.productId)
      ? this.manifest.hardware.productId
      : [this.manifest.hardware.productId]

    // HID 枚举启发式（兜底）：多 PID 列表第二个为无线接收器 PID，或产品名含 Receiver/Dongle。
    // 注意：单 PID manifest 时无法靠 PID 区分，需依赖 onConnected 后的协议连接模式（字节36）刷新。
    const isWireless =
      (pids.length > 1 && info.productId === pids[1]) ||
      Boolean(info.productName && /Receiver|Dongle/i.test(info.productName))

    this.connectionType = isWireless ? CONN_WIRELESS : CONN_WIRED
  }

  private tryDecodeReport(data: DataView): GcomeApFrame | null {
    try {
      return decodeGcomeApFrame(data)
    } catch (_error) {
      return null
    }
  }
}
